import { useEffect, useRef, useState } from 'react';
import { FiVolume2, FiVolumeX, FiMaximize } from 'react-icons/fi';

let apiPromise;
function youtubeAPI() {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (!apiPromise) apiPromise = new Promise((resolve, reject) => {
    const previous = window.onYouTubeIframeAPIReady;
    const timer = setTimeout(() => { apiPromise = null; reject(new Error('Player timed out')); }, 15000);
    window.onYouTubeIframeAPIReady = () => { clearTimeout(timer); previous?.(); resolve(window.YT); };
    const script = document.createElement('script'); script.src = 'https://www.youtube.com/iframe_api';
    script.onerror = () => { clearTimeout(timer); apiPromise = null; reject(new Error('Player unavailable')); };
    document.head.appendChild(script);
  });
  return apiPromise;
}
function savedMuted() {
  try { return localStorage.getItem('hedone-shorts-muted') !== 'false'; } catch { return true; }
}
export default function ReelPlayer({ video, title, vertical = false, suspended = false, onClipChange, hasMoreClips = false }) {
  const host = useRef(null);
  const player = useRef(null);
  const playbackActive = useRef(false);
  const desiredMuted = useRef(savedMuted());
  const current = useRef({ video, title, suspended });
  current.current = { video, title, suspended };
  const loaded = useRef(null);
  const wasSuspended = useRef(false);
  const resume = useRef(false);
  const blockedFallback = useRef(false);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(desiredMuted.current);
  const [fit, setFit] = useState(true);
  const gesture = useRef(null);
  const suppressClick = useRef(false);
  const [drag, setDrag] = useState(0);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let disposed = false;
    let timer;
    setReady(false); setError(false);
    const element = document.createElement('div'); host.current.appendChild(element);
    youtubeAPI().then(YT => {
      if (disposed) return;
      player.current = new YT.Player(element, {
        host: 'https://www.youtube-nocookie.com',
        playerVars: { controls: 0, iv_load_policy: 3, playsinline: 1, rel: 0, origin: window.location.origin, disablekb: 1 },
        events: {
          onReady: event => {
            if (disposed) return;
            event.target.getIframe().setAttribute('allow', 'autoplay; encrypted-media; fullscreen; picture-in-picture');
            setReady(true);
            timer = setInterval(() => {
              const p = player.current;
              if (!p?.getCurrentTime || current.current.suspended) return;
              const limit = Math.min(p.getDuration() || 60, 60);
              const time = p.getCurrentTime(); setProgress(Math.min(time / limit, 1));
              if (time >= limit && playbackActive.current && !document.hidden) { p.seekTo(0, true); p.playVideo(); }
            }, 250);
          },
          onStateChange: event => {
            if (disposed) return;
            playbackActive.current = event.data === 1; setPlaying(event.data === 1);
            if (event.data === 1 && (document.hidden || current.current.suspended)) event.target.pauseVideo();
            if (event.data === 0 && !document.hidden && !current.current.suspended) { event.target.seekTo(0, true); event.target.playVideo(); }
          },
          onAutoplayBlocked: event => {
            if (disposed || blockedFallback.current || current.current.suspended || document.hidden) return;
            blockedFallback.current = true; event.target.mute(); setMuted(true); event.target.playVideo();
          },
          onError: () => { if (!disposed) setError(true); },
        },
      });
    }).catch(() => { if (!disposed) setError(true); });
    const visibility = () => {
      if (document.hidden) { resume.current = playbackActive.current; player.current?.pauseVideo?.(); }
      else if (resume.current && !current.current.suspended) { player.current?.playVideo?.(); resume.current = false; }
    };
    document.addEventListener('visibilitychange', visibility);
    return () => { disposed = true; clearInterval(timer); document.removeEventListener('visibilitychange', visibility); player.current?.destroy?.(); player.current = null; loaded.current = null; element.remove(); };
  }, [attempt]);
  useEffect(() => {
    if (!ready || !player.current) return;
    const p = player.current;
    if (suspended || !video) { wasSuspended.current = true; p.pauseVideo(); return; }
    p.getIframe().title = `${title} reel preview`;
    if (loaded.current === video) {
      if (wasSuspended.current && !document.hidden) p.playVideo();
      wasSuspended.current = false;
      return;
    }
    wasSuspended.current = false;
    loaded.current = video; blockedFallback.current = false;
    setProgress(0); setError(false); setPlaying(false); playbackActive.current = false;
    desiredMuted.current ? p.mute() : p.unMute(); setMuted(desiredMuted.current);
    if (document.hidden) { resume.current = true; p.cueVideoById(video); }
    else { p.loadVideoById(video); p.playVideo(); }
  }, [video, title, ready, suspended]);
  const toggle = () => { if (!ready) return; playing ? player.current.pauseVideo() : player.current.playVideo(); };
  const endSwipe = event => {
    const start = gesture.current;
    gesture.current = null; setDrag(0);
    if (!start) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) >= 48 && Math.abs(dx) > Math.abs(dy) * 1.4) {
      suppressClick.current = true;
      onClipChange?.(dx < 0 ? 1 : -1);
    }
  };
  const toggleSound = () => {
    const next = !muted;
    desiredMuted.current = next;
    try { localStorage.setItem('hedone-shorts-muted', String(next)); } catch { /* Session preference still works. */ }
    next ? player.current.mute() : player.current.unMute(); setMuted(next);
    if (!playing) player.current.playVideo();
  };
  return <div className={`reel-video ${fit ? 'is-fit' : ''} ${vertical ? 'is-vertical' : ''}`} hidden={suspended}>
    <div className={`reel-video-host ${drag ? 'is-dragging' : ''}`} ref={host} style={{ translate: `${drag}px 0` }} />
    <button className="reel-touch" aria-label={playing ? 'Pause preview' : 'Play preview'}
      onPointerDown={event => {
        suppressClick.current = false;
        if (!hasMoreClips || !event.isPrimary || event.button !== 0) return;
        gesture.current = { x: event.clientX, y: event.clientY };
        event.currentTarget.setPointerCapture?.(event.pointerId);
      }}
      onPointerMove={event => {
        if (!gesture.current) return;
        const dx = event.clientX - gesture.current.x;
        const dy = event.clientY - gesture.current.y;
        if (Math.abs(dx) > Math.abs(dy) * 1.4) setDrag(Math.max(-64, Math.min(64, dx * .3)));
      }}
      onPointerUp={endSwipe}
      onPointerCancel={() => { gesture.current = null; setDrag(0); }}
      onClick={() => { if (suppressClick.current) { suppressClick.current = false; return; } toggle(); }}
      onKeyDown={event => { if (hasMoreClips && ['ArrowLeft', 'ArrowRight'].includes(event.key)) { event.preventDefault(); onClipChange?.(event.key === 'ArrowRight' ? 1 : -1); } }}
      disabled={!ready || error} />
    {!ready && !error && <span className="reel-loading" role="status">Loading preview…</span>}
    {error && <div className="reel-failure" role="status"><p>This preview is unavailable.</p><button onClick={() => setAttempt(value => value + 1)}>Try again</button><a href={`https://www.youtube.com/watch?v=${video}`} target="_blank" rel="noopener noreferrer">Open on YouTube</a></div>}
    <div className="reel-playback"><button disabled={!ready || error} aria-label={muted ? 'Unmute preview' : 'Mute preview'} onClick={toggleSound}>{muted ? <FiVolumeX /> : <FiVolume2 />}</button><button onClick={() => setFit(value => !value)} aria-label={fit ? 'Fill screen' : 'Fit full video'} aria-pressed={fit}><FiMaximize /></button></div>
    <div className="reel-progress" aria-hidden="true"><span style={{ transform: `scaleX(${progress})` }} /></div>
  </div>;
}
